import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { Box, IconButton, Typography } from "@material-ui/core";
import { Check, ChevronLeft, ChevronRight } from "@material-ui/icons";
import { useSelectedTheme } from "../../../../../Hooks";

import {
  CustomButton,
  PredefinedPaymentPlanCardSkeleton,
} from "../../../../../Components";
import { CheckCircleIcon01, PlusIcon } from "../../../../../assets/icons";
import Divider from "../CPP_PropertyDetailsComponent/Divider";

import {
  createPropertyPaymentPlanDto,
  formatPaymentPlansForTableFields,
} from "../../../PaymentPlans/models";
import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";
import {
  GetAllPaymentPlans,
  GetPredefinedPaymentPlans,
} from "../../../../../Services/PaymentPlanServices";

import useStyles from "./styles";
import PaymentPlanDialog from "../../../NewPropertiesView/PropertyOverview/UI/PaymentPlan/UI/PaymentPlanDialog/PaymentPlanDialog";
import { CreatePaymentPlan } from "../../../../../Services";
import { CompletePropertyProfileContext } from "../../../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext";
import { useTranslate } from "../../../../../Hooks";

function CPP_PaymentPlanComponent({ propertyId, stepId, onStepComplete }) {
  const styles = useStyles();
  const containerRef = useRef();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { setAlertBoxContent } = useVerticalNav();
  const context = useContext(CompletePropertyProfileContext);
  const { translate } = useTranslate("CompletePropertyProfile");
  const scrollLeft = () =>
    containerRef.current.scrollBy({
      left: -containerRef.current.offsetWidth,
      behavior: "smooth",
    });

  const scrollRight = () =>
    containerRef.current.scrollBy({
      left: containerRef.current.offsetWidth,
      behavior: "smooth",
    });

  const initialFilterWithOperator = {
    downPayment: { operator: null, value: "" },
    handoverPayment: { operator: null, value: "" },
  };
  const [IsOpenAddPaymentPlanMenu, setIsOpenAddPaymentPlanMenu] =
    useState(false);

  const initialFilter = {
    developerName: null,
    paymentPlanName: null,
    propertyPlanStatus: null,
    isForSpecificProperty: !!propertyId,
  };

  const [isLoading, setIsLoading] = useState(false);
  const [paymentPlansData, setPaymentPlansData] = useState({
    result: [],
    totalCount: 0,
  });
  const [filterWithOperator, setFilterWithOperator] = useState(
    initialFilterWithOperator
  );
  const [filter, setFilter] = useState(initialFilter);
  const [selectedPlans, setSelectedPlans] = useState({});
  const [hasSelectedPlan, setHasSelectedPlan] = useState(false);

  const getAllPaymentPlans = useCallback(async () => {
    setIsLoading(true);

    const advanceSearch = {};
    if (filterWithOperator.downPayment.value) {
      advanceSearch.downPayment = filterWithOperator.downPayment.value / 100;
      advanceSearch.searchType = filterWithOperator.downPayment.operator;
    }
    if (filterWithOperator.handoverPayment.value) {
      advanceSearch.handoverInstallment =
        filterWithOperator.handoverPayment.value / 100;
      advanceSearch.searchType = filterWithOperator.handoverPayment.operator;
    }

    const body = {
      pageIndex: 1,
      pageSize: 50,
      propertyId: Number(propertyId),
      ...filter,
      ...advanceSearch,
    };

    try {
      const [allRes, predefinedRes] = await Promise.all([
        GetAllPaymentPlans(body),
        GetPredefinedPaymentPlans({
          pageIndex: 1,
          pageSize: 50,
          propertyId: Number(propertyId),
        }),
      ]);

      const allPlans = formatPaymentPlansForTableFields(allRes.result);
      const predefinedPlans = formatPaymentPlansForTableFields(
        predefinedRes.result
      );

      const mergedResult = [...allPlans, ...predefinedPlans];

      setPaymentPlansData({
        result: mergedResult,
        totalCount: allRes.totalCount + predefinedRes.totalCount,
      });

      const initialSelected = {};
      let hasSelected = false;
      (predefinedPlans || []).forEach((plan) => {
        if (plan.isConfigurationSelected) {
          initialSelected[plan.propertyPaymentPlanId] = true;
          hasSelected = true;
        }
      });
      (allPlans || []).forEach((plan) => {
        if (plan.isForSpecificProperty) {
          initialSelected[plan.propertyPaymentPlanId] = true;
          hasSelected = true;
        }
      });
      setSelectedPlans(initialSelected);
      setHasSelectedPlan(hasSelected);
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("error-loading-payment-plans"),
        onClose: () => setAlertBoxContent(null),
      });
    } finally {
      setIsLoading(false);
    }
  }, [filter, filterWithOperator, propertyId, setAlertBoxContent, translate]);

  const onSelectedPlan = async (plan) => {
    setSelectedPlans((prev) => ({
      ...prev,
      [plan.propertyPaymentPlanId]: true,
    }));
    const result = await handleCreatePaymentPlan(plan);
    if (result.success) {
      setHasSelectedPlan(true);
    }
  };

  const handleCreatePaymentPlan = useCallback(
    async (selectedPlan) => {
      setIsLoading(true);
      try {
        await CreatePaymentPlan(
          createPropertyPaymentPlanDto({
            ...selectedPlan,
            propertyId: Number(propertyId),
          })
        );

        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("payment-plan-created-successfully"),
          onClose: () => setAlertBoxContent(null),
        });
        return { success: true };
      } catch (error) {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("error-creating-payment-plan"),
          onClose: () => setAlertBoxContent(null),
        });
        return { success: false, error: error.message };
      } finally {
        setIsLoading(false);
      }
    },
    [propertyId, setAlertBoxContent, translate]
  );

  const validateForm = useCallback(() => {
    if (!hasSelectedPlan) {
      return {
        isValid: false,
        errors: [{ message: translate("please-select-payment-plan") }],
      };
    }
    return { isValid: true, errors: [] };
  }, [hasSelectedPlan, translate]);

  const onSaveClicked = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: validation.errors[0].message,
        onClose: () => setAlertBoxContent(null),
      });
      return { success: false, errors: validation.errors };
    }

    if (onStepComplete) {
      onStepComplete();
    }

    setAlertBoxContent({
      display: true,
      variant: "success",
      title: translate("payment-plan-saved-successfully"),
      onClose: () => setAlertBoxContent(null),
    });
    return { success: true };
  }, [validateForm, onStepComplete, setAlertBoxContent, translate]);

  const onNextClicked = useCallback(async () => {
    return await onSaveClicked();
  }, [onSaveClicked]);

  useEffect(() => {
    if (propertyId) getAllPaymentPlans();
  }, [propertyId, getAllPaymentPlans]);

  useEffect(() => {
    if (context?.setSaveHandler) context.setSaveHandler(onSaveClicked);
    if (context?.setNextHandler) context.setNextHandler(onNextClicked);
    if (context?.setValidationHandler)
      context.setValidationHandler(validateForm);

    return () => {
      if (context?.setSaveHandler) context.setSaveHandler(null);
      if (context?.setNextHandler) context.setNextHandler(null);
      if (context?.setValidationHandler) context.setValidationHandler(null);
    };
  }, [onSaveClicked, onNextClicked, validateForm, context]);

  return (
    <Box className={styles.container}>
      {isLoading ? (
        <PredefinedPaymentPlanCardSkeleton numberOfCards={2} />
      ) : (
        <>
          <Box className={styles.header}>
            <span className={styles.title}>Payment plans</span>
            <CustomButton
              variant="text"
              boxShadow="none"
              color=""
              hideHoverBg
              onClick={() => setIsOpenAddPaymentPlanMenu(true)}
              startIcon={
                <PlusIcon
                  width="20"
                  height="20"
                  fill={palette.button.tertiaryGray_fg}
                />
              }
            >
              <span className={styles.textBtn}>Add new payment plan</span>
            </CustomButton>
          </Box>

          <Divider />
          <Box className={styles.cardsWrapper} ref={containerRef}>
            {paymentPlansData?.result.map((item, index) => (
              <Box key={index} className={styles.card}>
                <Box className={styles.cardHeader}>
                  <span className={styles.cardTitle}>
                    {item.paymentPlanName}
                  </span>
                  <span>{item?.description}</span>
                </Box>

                <Box className={styles.cardBody}>
                  <Box className={styles.installmentDetail}>
                    <Box className={styles.planItem}>
                      <CheckCircleIcon01
                        width="24"
                        height="24"
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />
                      <Typography className={styles.installmentText}>
                        {item?.downPayment}
                        Down Payment
                      </Typography>
                    </Box>
                    <Box className={styles.planItem}>
                      <CheckCircleIcon01
                        width="24"
                        height="24"
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />
                      <Typography className={styles.installmentText}>
                        {item?.monthlyInstallment} Monthly Installment
                      </Typography>
                    </Box>
                    <Box className={styles.planItem}>
                      <CheckCircleIcon01
                        width="24"
                        height="24"
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />
                      <Typography className={styles.installmentText}>
                        {item?.handoverPayment} Handover Payment
                      </Typography>
                    </Box>
                    <Box className={styles.planItem}>
                      <CheckCircleIcon01
                        width="24"
                        height="24"
                        background={palette.background.success_secondary}
                        foreground={palette.foreground.success}
                      />
                      <Typography className={styles.installmentText}>
                        {item?.postHandover} Post Handover
                      </Typography>
                    </Box>
                  </Box>
                  <Box className={styles.actionBtnContainer}>
                    {item?.isForSpecificProperty ||
                    selectedPlans[item.propertyPaymentPlanId] ? (
                      <CustomButton
                        color="primary"
                        startIcon={<Check fontSize="small" />}
                        disabled
                      >
                        {translate("selected")}
                      </CustomButton>
                    ) : (
                      <CustomButton
                        color="secondary"
                        variant="outlined"
                        onClick={() => onSelectedPlan(item)}
                      >
                        {translate("select")}
                      </CustomButton>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
          {paymentPlansData?.result.length > 2 && (
            <Box className={styles.scrollArrows}>
              <Box>
                <IconButton className={styles.IconButton} onClick={scrollLeft}>
                  <ChevronLeft />
                </IconButton>
              </Box>
              <Box>
                <IconButton className={styles.IconButton} onClick={scrollRight}>
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          )}
        </>
      )}
      {IsOpenAddPaymentPlanMenu && (
        <PaymentPlanDialog
          onOpen={IsOpenAddPaymentPlanMenu}
          onClose={() => {
            setIsOpenAddPaymentPlanMenu(false);
          }}
          propertyId={propertyId}
          reloadData={() => getAllPaymentPlans()}
        />
      )}
    </Box>
  );
}

export default CPP_PaymentPlanComponent;
